import React, { Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AuthPage } from '../AuthPage';
import CompactPlanPicker from './CompactPlanPicker';
import { TemplateSelection } from '../TemplateSelection';
import type { PaymentPlan } from '../PaymentPanel';
import { defaultAuthenticationSettings } from '../../data/mockData';
import { updateUser } from '../../api/users';
import safeToast from '../../utils/safeToast';
import {
  markMiniwebsiteSignupComplete,
  markMiniwebsiteSignupSource,
} from '../../utils/signupRedirect';
import { waitForOwnerSession, refreshSignupProfileFromStorage } from '../../utils/ownerSession';

const PaymentPanel = React.lazy(() => import('../PaymentPanel'));

type FlowStep = 'plan' | 'signup' | 'template' | 'payment';

const STEPS: FlowStep[] = ['plan', 'signup', 'template', 'payment'];

function parsePlanAmount(price?: string): number {
  try {
    return Number(String(price || '').replace(/[^0-9.]/g, '')) || 0;
  } catch {
    return 0;
  }
}

function rememberTemplateFallback(email?: string | null, fallbackId?: string | null) {
  if (!email || !fallbackId) return;
  const key = `template_fallback:${String(email).trim().toLowerCase()}`;
  try {
    localStorage.setItem(key, fallbackId);
  } catch {}
}

function clearMiniSignupTemp(email?: string | null) {
  try {
    localStorage.removeItem('signup_progress_v1');
    localStorage.removeItem('template_selection_user');
    localStorage.removeItem('selectedPlan');
    if (email) {
      const e = String(email).trim().toLowerCase();
      localStorage.removeItem(`template_selection:${e}`);
      localStorage.removeItem(`template_selection_category:${e}`);
      localStorage.removeItem(`pending_template_patch:${e}`);
    }
  } catch {}
}

async function waitForOwnerSessionWithProfile(profile: Record<string, any> | null, timeoutMs = 10000): Promise<boolean> {
  return waitForOwnerSession(
    {
      email: profile?.email,
      password: profile?.password,
      username: profile?.username,
      id: profile?.id,
      _id: profile?._id,
    },
    timeoutMs,
  );
}

interface CardOwnerSignupModalProps {
  open: boolean;
  onClose: () => void;
}

export const CardOwnerSignupModal: React.FC<CardOwnerSignupModalProps> = ({ open, onClose }) => {
  const { t } = useTranslation();
  const [step, setStep] = useState<FlowStep>('plan');
  const [plans, setPlans] = useState<any[]>([]);
  const [loadingPlans, setLoadingPlans] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState<PaymentPlan | null>(null);
  const [userData, setUserData] = useState<any>(null);
  const [preparingPayment, setPreparingPayment] = useState(false);

  const paidPlan = useMemo(() => parsePlanAmount(selectedPlan?.price) > 0, [selectedPlan]);

  const visibleSteps = useMemo(
    () => (paidPlan ? STEPS : STEPS.filter((s) => s !== 'payment')),
    [paidPlan],
  );

  const finishSignup = useCallback(
    async (_paymentSucceeded: boolean) => {
      let profile: any = userData;
      try {
        const raw = localStorage.getItem('template_selection_user');
        profile = raw ? JSON.parse(raw) : userData;
      } catch {}

      let ready = await waitForOwnerSessionWithProfile(profile);
      if (!ready) {
        safeToast('info', t('mws_signup_finalizing', { defaultValue: 'Finishing account setup…' }));
        ready = await waitForOwnerSessionWithProfile(profile, 15000);
      }
      if (!ready) {
        safeToast('error', t('mws_signup_session_timeout', { defaultValue: 'Account setup is taking longer than expected. Please try again in a moment.' }));
        return;
      }

      try {
        markMiniwebsiteSignupComplete();
        clearMiniSignupTemp(profile?.email || null);
      } catch {}

      onClose();
      window.location.assign('/website/dashboard');
    },
    [onClose, userData, t],
  );

  useEffect(() => {
    if (!open) return;
    markMiniwebsiteSignupSource();
    setStep('plan');
    setSelectedPlan(null);
    setUserData(null);
    setLoadingPlans(true);
    (async () => {
      try {
        const resp = await fetch('/api/public/subscription-plans');
        if (resp.ok) {
          const data = await resp.json();
          if (Array.isArray(data) && data.length > 0) {
            setPlans(data);
            try {
              localStorage.setItem('server_plans_cache', JSON.stringify(data));
            } catch {}
            return;
          }
        }
      } catch {}
      try {
        const raw = localStorage.getItem('server_plans_cache');
        if (raw) {
          const cached = JSON.parse(raw);
          if (Array.isArray(cached) && cached.length > 0) {
            setPlans(cached);
            return;
          }
        }
      } catch {}
      setPlans([]);
    })().finally(() => setLoadingPlans(false));
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  const handlePlanChoose = (plan: PaymentPlan) => {
    setSelectedPlan(plan);
    try {
      localStorage.setItem('selectedPlan', JSON.stringify(plan));
    } catch {}
    setStep('signup');
  };

  const handleUserSignup = (signupData: any) => {
    try {
      localStorage.setItem('template_selection_user', JSON.stringify(signupData));
    } catch {}
    setUserData(signupData);
    setStep('template');
  };

  const handleTemplateSelect = useCallback(
    async (payload: { templateId?: string; templateMetaId?: string; templateMeta?: Record<string, any>; componentKey?: string; variant?: string } | string) => {
      if (!userData) return;

      const hasMeta = typeof payload !== 'string' && !!payload?.templateMetaId;
      const templateId = hasMeta ? null : typeof payload === 'string' ? payload : payload?.templateId || '1';
      const templateMetaId = typeof payload === 'string' ? undefined : payload?.templateMetaId;
      const templateMeta = typeof payload === 'string' ? undefined : payload?.templateMeta;

      const templateIdToKeyMap: Record<string, string> = {
        '1': 'professional-classic',
        '2': 'elegant-looking',
        '3': 'modern-clean',
        '4': 'golden-view',
        '5': 'classic-modular',
      };
      const componentKeyFromPayload =
        typeof payload !== 'string' ? payload?.componentKey || (payload?.templateMeta as any)?.componentKey : undefined;
      const knownClassicKeys = ['professional-classic', 'elegant-looking', 'modern-clean', 'golden-view', 'classic-modular'];
      const templateKey = (() => {
        if (componentKeyFromPayload && knownClassicKeys.includes(String(componentKeyFromPayload).toLowerCase())) {
          return String(componentKeyFromPayload).toLowerCase();
        }
        if (templateId && templateIdToKeyMap[templateId]) return templateIdToKeyMap[templateId];
        return undefined;
      })();

      if (templateMeta && templateMetaId) {
        const normalizedMeta = { ...(templateMeta as any), id: templateMetaId };
        try {
          const cacheKey = 'templates_cache_meta';
          const raw = localStorage.getItem(cacheKey);
          const existing = raw ? JSON.parse(raw) : {};
          localStorage.setItem(cacheKey, JSON.stringify({ ...existing, [templateMetaId]: normalizedMeta }));
        } catch {}
        try {
          (window as any).__TEMPLATES_META_BY_ID__ = {
            ...(window as any).__TEMPLATES_META_BY_ID__,
            [templateMetaId]: normalizedMeta,
          };
        } catch {}
      }

      const fallbackTemplateId = (() => {
        if (typeof payload === 'string' && /^[1-5]$/.test(payload)) return payload;
        if (typeof payload !== 'string') {
          if (payload?.templateId && /^[1-5]$/.test(String(payload.templateId))) return String(payload.templateId);
          if (templateKey && templateIdToKeyMap) {
            const entry = Object.entries(templateIdToKeyMap).find(([, v]) => v === templateKey);
            if (entry) return entry[0];
          }
        }
        return null;
      })();
      if (fallbackTemplateId) rememberTemplateFallback((userData as any)?.email, fallbackTemplateId);

      const effectiveTemplateId = templateId || fallbackTemplateId;
      try {
        const raw = localStorage.getItem('template_selection_user');
        const local = raw ? JSON.parse(raw) : userData || {};
        const updated = {
          ...(local || {}),
          templateId: effectiveTemplateId,
          ...(templateKey ? { templateKey } : {}),
          ...(templateMetaId ? { templateMetaId } : { templateMetaId: null }),
          ...(fallbackTemplateId ? { templateClassicFallbackId: fallbackTemplateId } : {}),
        };
        localStorage.setItem('template_selection_user', JSON.stringify(updated));
      } catch {}

      try {
        const patch = {
          templateId: effectiveTemplateId || undefined,
          templateKey,
          templateMetaId: templateMetaId || undefined,
        };
        const token = localStorage.getItem('owner_token');
        if (!token) {
          const emailLower = String((userData as any)?.email || '').trim().toLowerCase();
          if (emailLower) {
            localStorage.setItem(`pending_template_patch:${emailLower}`, JSON.stringify(patch));
          }
        }
      } catch {}

      const finalizeAfterTemplate = async (): Promise<any | null> => {
        if (paidPlan) {
          safeToast('info', t('mws_signup_finalizing', { defaultValue: 'Finishing account setup…' }));
        }
        const sessionReady = await waitForOwnerSessionWithProfile(userData, paidPlan ? 15000 : 10000);
        if (!sessionReady) {
          safeToast('error', t('mws_signup_session_timeout', { defaultValue: 'Could not finish account setup. Please wait a moment and try Continue again.' }));
          return null;
        }
        const refreshed = refreshSignupProfileFromStorage(userData);
        setUserData(refreshed);

        try {
          const patch = {
            templateId: effectiveTemplateId || undefined,
            templateKey,
            templateMetaId: templateMetaId || undefined,
          };
          const token = localStorage.getItem('owner_token');
          const userId = refreshed.id || refreshed._id;
          if (token && userId && /^[0-9a-fA-F]{24}$/.test(String(userId))) {
            await updateUser(String(userId), patch);
          } else {
            const emailLower = String(refreshed?.email || '').trim().toLowerCase();
            if (emailLower) {
              localStorage.setItem(`pending_template_patch:${emailLower}`, JSON.stringify(patch));
            }
          }
        } catch {}

        return refreshed;
      };

      if (paidPlan) {
        setPreparingPayment(true);
        try {
          const refreshed = await finalizeAfterTemplate();
          if (refreshed) setStep('payment');
        } finally {
          setPreparingPayment(false);
        }
      } else {
        const refreshed = await finalizeAfterTemplate();
        if (!refreshed) return;
        safeToast('success', t('mws_free_plan_active', { defaultValue: 'Your free account is ready!' }));
        await finishSignup(false);
      }
    },
    [userData, paidPlan, finishSignup, t],
  );

  const stepIndex = visibleSteps.indexOf(step);
  const stepLabels: Record<FlowStep, string> = {
    plan: t('mws_step_plan', { defaultValue: 'Package' }),
    signup: t('mws_step_account', { defaultValue: 'Account' }),
    template: t('mws_step_template', { defaultValue: 'Template' }),
    payment: t('mws_step_payment', { defaultValue: 'Payment' }),
  };

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t('mws_modal_title', { defaultValue: 'Create your digital card' })}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 10050,
        background: 'rgba(15, 23, 42, 0.62)',
        backdropFilter: 'blur(4px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}
      onClick={onClose}
    >
      <div
        style={{
          width: '100%',
          maxWidth: step === 'template' ? 980 : step === 'payment' ? 920 : step === 'signup' ? 720 : step === 'plan' ? 680 : 760,
          maxHeight: '92vh',
          background: '#FFF',
          borderRadius: 20,
          boxShadow: '0 24px 80px rgba(0,0,0,0.28)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            padding: '18px 22px 14px',
            borderBottom: '1px solid #E2E8F0',
            background: 'linear-gradient(135deg, #EFF6FF 0%, #F5F3FF 100%)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
            <div>
              <h2 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: '#0F172A' }}>
                {t('mws_modal_title', { defaultValue: 'Create your digital card' })}
              </h2>
              <p style={{ margin: '4px 0 0', fontSize: 13, color: '#64748B' }}>
                {t('mws_modal_subtitle', { defaultValue: 'Choose a plan, create your account, and launch your card in minutes.' })}
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label={t('mw_close', { defaultValue: 'Close' })}
              style={{
                width: 36,
                height: 36,
                borderRadius: 10,
                border: '1px solid #E2E8F0',
                background: '#FFF',
                color: '#64748B',
                cursor: 'pointer',
                fontSize: 18,
                lineHeight: 1,
              }}
            >
              ×
            </button>
          </div>

          {/* Step indicator */}
          <div style={{ display: 'flex', gap: 6, marginTop: 16 }}>
            {visibleSteps.map((s, idx) => {
              const active = s === step;
              const done = idx < stepIndex;
              return (
                <div key={s} style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      height: 4,
                      borderRadius: 999,
                      background: done || active ? '#3B82F6' : '#E2E8F0',
                      opacity: active ? 1 : done ? 0.85 : 1,
                    }}
                  />
                  <p
                    style={{
                      margin: '6px 0 0',
                      fontSize: 11,
                      fontWeight: active ? 700 : 500,
                      color: active ? '#1D4ED8' : done ? '#64748B' : '#94A3B8',
                      textAlign: 'center',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {stepLabels[s]}
                  </p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflow: 'auto', padding: step === 'signup' ? '16px 22px' : step === 'plan' ? '20px 22px' : step === 'payment' ? '20px 22px' : '20px 22px' }}>
          {step === 'plan' && (
            loadingPlans ? (
              <div style={{ textAlign: 'center', padding: 48, color: '#94A3B8' }}>
                {t('mws_loading_plans', { defaultValue: 'Loading packages…' })}
              </div>
            ) : (
              <CompactPlanPicker plans={plans} onChoose={handlePlanChoose} />
            )
          )}

          {step === 'signup' && (
            <AuthPage
              authType="user"
              initialView="signup"
              embedded
              compact
              signupOnly
              hideExit
              backLabel={t('mws_back_packages', { defaultValue: 'Back to packages' })}
              onBack={() => setStep('plan')}
              onAdminLogin={() => {}}
              onUserSignup={handleUserSignup}
              authSettings={defaultAuthenticationSettings}
            />
          )}

          {step === 'template' && userData && (
            <>
              {preparingPayment && (
                <div style={{ padding: 32, textAlign: 'center', color: '#64748B' }}>
                  {t('mws_signup_finalizing', { defaultValue: 'Finishing account setup…' })}
                </div>
              )}
              <TemplateSelection
                userData={userData}
                onTemplateSelect={handleTemplateSelect}
                onBack={() => setStep('signup')}
                embedded
              />
            </>
          )}

          {step === 'payment' && userData && selectedPlan && (
            <Suspense fallback={<div style={{ padding: 32, textAlign: 'center', color: '#94A3B8' }}>{t('mw_processing')}</div>}>
              <PaymentPanel
                embedded
                user={{
                  id: userData?.id || userData?._id,
                  _id: userData?._id || userData?.id,
                  name: userData?.name,
                  email: userData?.email,
                  phone: userData?.phone,
                  position: userData?.position,
                  company: userData?.company,
                  location: userData?.location,
                }}
                plan={selectedPlan}
                onSuccess={() => {
                  safeToast('success', t('mws_payment_success_redirect', { defaultValue: 'Payment successful! Opening your card panel…' }));
                  void finishSignup(true);
                }}
                onFailure={() => {
                  safeToast('info', t('mws_payment_failed_free', { defaultValue: 'Payment was not completed. You can continue on the free plan — we will email you upgrade options.' }));
                  void finishSignup(false);
                }}
                onCancel={() => setStep('template')}
              />
            </Suspense>
          )}
        </div>
      </div>
    </div>
  );
};

export default CardOwnerSignupModal;
